export type GuideStatus = 'available' | 'blueprint';

export type GuideSection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  steps?: Array<{ title: string; detail: string }>;
  code?: { label: string; value: string };
  table?: { headers: string[]; rows: string[][] };
  links?: Array<{ label: string; href: string; detail?: string }>;
  note?: { tone: 'info' | 'warn' | 'success'; title: string; body: string };
};

export type GuideArticle = {
  id: string;
  category:
    | 'Start here'
    | 'Delivery engineering'
    | 'Release configuration'
    | 'Integrations'
    | 'Operations';
  title: string;
  summary: string;
  status: GuideStatus;
  minutes: number;
  tags: string[];
  sections: GuideSection[];
  related?: string[];
};

export const GUIDE_CATEGORIES: GuideArticle['category'][] = [
  'Start here',
  'Delivery engineering',
  'Release configuration',
  'Integrations',
  'Operations'
];

export const GUIDE_ARTICLES: GuideArticle[] = [
  {
    id: 'start-here',
    category: 'Start here',
    title: 'Set up Cindro in the right order',
    status: 'available',
    minutes: 6,
    summary:
      'Set up a workspace that follows real work from task to production.',
    tags: ['setup', 'onboarding', 'workspace', 'project'],
    related: ['workspace-model', 'github-connection', 'delivery-tracks'],
    sections: [
      {
        id: 'outcome',
        title: 'What Cindro does',
        paragraphs: [
          'GitHub still runs CI. Pulumi or another deployment tool still changes infrastructure. Apple and Google still review native releases. Cindro joins those records so you can see where work stopped and run the next configured step.',
          'When setup is complete, the workspace shows one delivery path across your projects and an Inbox with the work that needs attention.'
        ]
      },
      {
        id: 'sequence',
        title: 'Recommended setup sequence',
        steps: [
          {
            title: 'Create the workspace and projects',
            detail:
              'Model the products your team owns. A project is a product boundary, not a repository.'
          },
          {
            title: 'Register environments and release targets',
            detail:
              'Make staging and production explicit before connecting automation.'
          },
          {
            title: 'Connect every repository that contributes evidence',
            detail:
              'Include application, deployment and shared infrastructure repositories.'
          },
          {
            title: 'Activate a delivery model',
            detail:
              'Describe the expected DAG in Delivery model or import .cindro/pipeline.yml.'
          },
          {
            title: 'Check one real change',
            detail:
              'Sync recent history and confirm Radar stops at the right stage.'
          },
          {
            title: 'Enable actions',
            detail:
              'Then allow Cindro to run selected merges, promotions or OTA assignments.'
          }
        ]
      },
      {
        id: 'definition-of-done',
        title: 'A setup is ready when',
        bullets: [
          'A real change can be followed across every repository it touches.',
          'Missing evidence is shown as unknown or overdue, never complete.',
          'Staging and production are distinguishable by exact provider evidence.',
          'Every human gate names the owner and exact action.',
          'The active delivery definition is versioned and recoverable.'
        ],
        note: {
          tone: 'warn',
          title: 'Do not start by enabling automation',
          body: 'Observe at least one real journey end to end first. A wrong correlation is inconvenient in read-only mode and dangerous in dispatch mode.'
        }
      }
    ]
  },
  {
    id: 'workspace-model',
    category: 'Start here',
    title: 'Workspaces, projects, apps and environments',
    status: 'available',
    minutes: 5,
    summary:
      'Choose boundaries that stay understandable when work spans several repositories.',
    tags: ['workspace', 'project', 'app', 'environment', 'scope'],
    related: ['repository-layout', 'environments'],
    sections: [
      {
        id: 'boundaries',
        title: 'Use product boundaries, not repository boundaries',
        table: {
          headers: ['Cindro object', 'Use it for', 'Avoid'],
          rows: [
            [
              'Workspace',
              'One engineering organisation and its shared delivery systems',
              'A single repository'
            ],
            [
              'Project',
              'A product or independently operated service boundary',
              'Creating one per frontend/backend repo'
            ],
            [
              'App',
              'A distributable web, iOS or Android application',
              'An arbitrary deployment component'
            ],
            [
              'Environment',
              'A meaningful operating stage such as staging or production',
              'Every ephemeral branch unless the team operates it'
            ]
          ]
        }
      },
      {
        id: 'cross-repo',
        title: 'Cross-repository work belongs to one journey',
        paragraphs: [
          'A Skillora feature may start in the application repository, produce an image, update skillora-deploy, and depend on platform-infra. Those are evidence sources for one product journey—not four Cindro projects.',
          'Link shared repositories once at workspace level and declare every affected project. Cindro can then correlate several tasks and pull requests into the same downstream deployment.'
        ]
      },
      {
        id: 'scope',
        title: 'Know which scope you are in',
        bullets: [
          'Work Radar, Delivery model and Guide are workspace-wide.',
          'Release configuration pages operate on the selected project.',
          'Apps and app versions sit inside a project.',
          'Environment visibility is a UI preference; it does not delete environment records.'
        ]
      }
    ]
  },
  {
    id: 'repository-layout',
    category: 'Delivery engineering',
    title: 'Model application, deploy and infrastructure repositories',
    status: 'available',
    minutes: 6,
    summary:
      'Connect multi-repository delivery without inventing projects for every repository.',
    tags: ['repository', 'iac', 'pulumi', 'monorepo', 'deploy'],
    related: ['github-connection', 'delivery-tracks'],
    sections: [
      {
        id: 'recommended',
        title: 'Recommended repository roles',
        table: {
          headers: ['Role', 'Typical contents', 'Evidence Cindro expects'],
          rows: [
            [
              'Application',
              'Source, tests, build workflows',
              'PRs, checks, workflow runs, build artifacts'
            ],
            [
              'Deployment',
              'Environment manifests or Pulumi stacks',
              'Promotion PRs, deploy workflows, GitHub deployments'
            ],
            [
              'Shared infrastructure',
              'Cluster, network and platform IaC',
              'Infrastructure workflow and deployment evidence'
            ],
            [
              'Distribution',
              'Fastlane or store metadata when separated',
              'Native build and submission evidence'
            ]
          ]
        }
      },
      {
        id: 'linking',
        title: 'Link every repository that can move a journey',
        paragraphs: [
          'Connect the application repository and every repository that can create, deploy, promote or verify its artifacts. Shared IaC such as platform-infra should be linked once and associated with each project it can affect.'
        ],
        bullets: [
          'Install the Cindro GitHub App on the repository.',
          'The user completing the link must have write or admin access.',
          'Select every project affected by the repository.',
          'Sync once after linking to backfill bounded recent evidence.'
        ]
      },
      {
        id: 'naming',
        title: 'Use stable correlation handles',
        bullets: [
          'Carry task references in branch names or PR descriptions.',
          'Preserve source and merge SHAs through build metadata.',
          'Pass cindro_journey_id to workflows dispatched by Cindro.',
          'Publish GitHub Deployment statuses with exact environment names.'
        ],
        code: {
          label: 'Branch and workflow correlation',
          value:
            'feat/CU-1234-coach-availability\n\n# workflow_dispatch input\ncindro_journey_id: ${{ inputs.cindro_journey_id }}'
        }
      }
    ]
  },
  {
    id: 'github-connection',
    category: 'Delivery engineering',
    title: 'Connect GitHub',
    status: 'available',
    minutes: 7,
    summary:
      'Grant the access Cindro needs to read delivery state and run approved workflows.',
    tags: ['github', 'app', 'webhook', 'permissions', 'actions'],
    related: ['repository-layout', 'delivery-tracks', 'work-radar'],
    sections: [
      {
        id: 'permissions',
        title: 'GitHub App permissions',
        table: {
          headers: ['Permission', 'Access', 'Why'],
          rows: [
            ['Metadata', 'Read', 'Resolve repository identity'],
            [
              'Pull requests',
              'Read and write',
              'Observe reviews and merge definition-bound PRs'
            ],
            [
              'Administration',
              'Read',
              'Verify branch protection and required review counts'
            ],
            [
              'Members',
              'Read',
              'Verify that an approving user belongs to a required CODEOWNERS team'
            ],
            ['Checks', 'Read', 'Observe CI outcomes'],
            [
              'Actions',
              'Read and write',
              'Observe runs and dispatch configured workflows'
            ],
            ['Deployments', 'Read', 'Observe environment delivery state'],
            [
              'Contents',
              'Read and write',
              'Read pipeline definitions and CODEOWNERS at the exact base revision'
            ]
          ]
        }
      },
      {
        id: 'events',
        title: 'Subscribe to the evidence Cindro models',
        bullets: [
          'Pull request',
          'Check run',
          'Workflow run',
          'Deployment status'
        ],
        note: {
          tone: 'info',
          title: 'Webhook delivery must be externally reachable',
          body: 'GitHub signs webhook requests with the configured secret. Cindro rejects unsigned or invalid deliveries rather than accepting ambiguous evidence.'
        }
      },
      {
        id: 'workflow-contract',
        title: 'Keep execution in GitHub Actions',
        paragraphs: [
          'A Cindro pipeline definition is not a GitHub Actions workflow. It describes expected evidence and safe dispatch targets; the repository workflow still owns build, test and deployment commands.'
        ],
        code: {
          label: 'Recommended manual workflow input',
          value:
            'on:\n  workflow_dispatch:\n    inputs:\n      cindro_journey_id:\n        description: Correlates this run to a Cindro journey\n        required: false\n        type: string'
        }
      }
    ]
  },
  {
    id: 'delivery-tracks',
    category: 'Delivery engineering',
    title: 'Design a delivery track',
    status: 'available',
    minutes: 9,
    summary: 'Describe the stages work passes through before it reaches users.',
    tags: ['pipeline', 'yaml', 'dag', 'stages', 'dispatch'],
    related: ['github-connection', 'work-radar'],
    sections: [
      {
        id: 'principle',
        title: 'Describe evidence, not implementation scripts',
        paragraphs: [
          'Each stage says what evidence satisfies it, what must complete first, who owns the wait, and optionally which connector action a person may dispatch. This keeps the model reusable across GitHub, future task trackers, stores and infrastructure providers.'
        ]
      },
      {
        id: 'stage',
        title: 'Define each stage',
        bullets: [
          'Give it a name the team already uses.',
          'List the stages that must finish first.',
          'Choose the GitHub, deployment or store records that complete it.',
          'Say whether a person, system or outside provider owns the wait.',
          'Set how long Cindro should wait before adding it to Inbox.'
        ]
      },
      {
        id: 'example',
        title: 'Minimal cross-repository track',
        code: {
          label: '.cindro/pipeline.yml',
          value:
            'version: 1\nname: Product delivery\nscope: project\n\nstages:\n  - key: source\n    label: Source change\n    waitingOn: human\n    evidence:\n      kinds: [pull_request]\n      repos: [your-org/your-app]\n      satisfiedBy: present\n\n  - key: verification\n    label: Verification\n    needs: [source]\n    waitingOn: system\n    attentionAfter: 1h\n    evidence:\n      kinds: [check, workflow]\n      repos: [your-org/your-app]\n      satisfiedBy: success\n\n  - key: staging\n    label: Staging deploy\n    needs: [verification]\n    waitingOn: system\n    evidence:\n      kinds: [deployment]\n      repos: [your-org/your-app-deploy]\n      environments: [your-app-staging]\n      satisfiedBy: success'
        }
      },
      {
        id: 'activation',
        title: 'Activation safety',
        bullets: [
          'Preview validates stage keys, dependencies, cycles and durations.',
          'Activation stores an immutable normalized revision.',
          'Repository sync never replaces the last valid definition with a missing or invalid file.',
          'Manual actions are derived server-side from the active definition; the browser cannot invent an executable target.'
        ]
      }
    ]
  },
  {
    id: 'environments',
    category: 'Release configuration',
    title: 'Configure environments and release targets',
    status: 'available',
    minutes: 6,
    summary: 'Define the environments your team deploys to and sees in Cindro.',
    tags: ['environment', 'staging', 'production', 'channels'],
    related: ['ota-delivery', 'runtime-rules'],
    sections: [
      {
        id: 'model',
        title: 'Use the names your deployment system reports',
        paragraphs: [
          'Create the environments your team actually operates: development, internal, staging and production. The name in GitHub or your deployment provider must match the name in the delivery track.'
        ]
      },
      {
        id: 'recommended',
        title: 'Recommended baseline',
        table: {
          headers: ['Environment', 'Purpose', 'Typical gate'],
          rows: [
            ['Development', 'Engineer-owned integration', 'Automated'],
            ['Internal', 'Team or QA distribution', 'Optional manual gate'],
            [
              'Staging',
              'Production-like validation',
              'Manual validation before promotion'
            ],
            ['Production', 'User-facing release', 'Protected approval']
          ]
        }
      },
      {
        id: 'visibility',
        title: 'Hide clutter without deleting history',
        paragraphs: [
          'Use environment visibility to hide unused targets from day-to-day selectors. Hidden environments remain available to historical records and can be restored at any time.'
        ],
        note: {
          tone: 'warn',
          title: 'Do not rename provider environments casually',
          body: 'A delivery model matching skillora-prod will not be satisfied by a deployment reported as production. Update the model and provider together.'
        }
      }
    ]
  },
  {
    id: 'ota-delivery',
    category: 'Release configuration',
    title: 'Set up OTA bundles and promotion',
    status: 'available',
    minutes: 8,
    summary: 'Configure apps, versions, bundles, channels and safe promotion.',
    tags: ['ota', 'bundle', 'channel', 'promotion', 'rollback'],
    related: ['app-versions', 'runtime-rules', 'environments'],
    sections: [
      {
        id: 'objects',
        title: 'The OTA release model',
        table: {
          headers: ['Object', 'Meaning'],
          rows: [
            ['App', 'One distributable web, iOS or Android application'],
            ['App version', 'A native or web compatibility boundary'],
            [
              'Bundle',
              'An immutable uploaded build with source and build identity'
            ],
            [
              'Channel',
              'The bundle currently served to an exact app version and environment'
            ],
            ['Promotion', 'The audit record of changing a channel target']
          ]
        }
      },
      {
        id: 'publish',
        title: 'Publish immutable bundles from CI',
        code: {
          label: 'CI publish example',
          value:
            'npx cindro publish \\\n  --project "$CINDRO_PROJECT" \\\n  --app "$CINDRO_APP" \\\n  --version "$APP_VERSION" \\\n  --commit "$GITHUB_SHA" \\\n  --branch "$GITHUB_REF_NAME" \\\n  ./dist'
        }
      },
      {
        id: 'promote',
        title: 'Promote; do not rebuild',
        bullets: [
          'Validate the exact bundle in staging.',
          'Promote the same immutable bundle to production.',
          'Keep the build version and commit available for cross-reference.',
          'Use Recovery to restore a previously served bundle without rebuilding.'
        ],
        note: {
          tone: 'success',
          title: 'The safe invariant',
          body: 'The artifact tested in staging must be the artifact promoted to production.'
        }
      }
    ]
  },
  {
    id: 'app-versions',
    category: 'Release configuration',
    title: 'Register app versions and compatibility',
    status: 'available',
    minutes: 5,
    summary:
      'Use app versions to define which native shells can safely receive a bundle.',
    tags: ['ios', 'android', 'version', 'native', 'compatibility'],
    related: ['ota-delivery', 'app-stores'],
    sections: [
      {
        id: 'when',
        title: 'Create a version for a compatibility boundary',
        paragraphs: [
          'Register a new app version when a native release changes capabilities, plugins or minimum requirements. OTA-only changes normally remain within the existing compatible native version.'
        ]
      },
      {
        id: 'identity',
        title: 'Keep identities aligned',
        bullets: [
          'Version matches the store/native version your users install.',
          'Platform is explicit: iOS, Android or web.',
          'Bundle metadata declares its minimum compatible native version.',
          'Runtime rules route only compatible clients to the bundle.'
        ]
      }
    ]
  },
  {
    id: 'runtime-rules',
    category: 'Release configuration',
    title: 'Configure runtime rules',
    status: 'available',
    minutes: 7,
    summary:
      'Return the correct API, CDN, analytics and OTA target to each client.',
    tags: ['runtime', 'config', 'cdn', 'api', 'analytics'],
    related: ['environments', 'domains', 'ota-delivery'],
    sections: [
      {
        id: 'matching',
        title: 'Prefer narrow, explicit matching',
        bullets: [
          'Match project, app version, platform and environment intentionally.',
          'Use priority only to resolve genuine overlaps.',
          'Keep a broad fallback rule below narrow production rules.',
          'Disable obsolete rules instead of silently repurposing them.'
        ]
      },
      {
        id: 'responsibility',
        title: 'Understand each field group',
        table: {
          headers: ['Group', 'Controls'],
          rows: [
            ['Identity', 'Name, platform and activation'],
            ['Endpoints', 'Application API and static CDN origins'],
            ['OTA', 'Environment and compatible app version'],
            ['Analytics', 'Provider keys and page-view behaviour'],
            ['Observability', 'Telemetry enablement and collector endpoint']
          ]
        }
      },
      {
        id: 'verify',
        title: 'Verify from the client perspective',
        paragraphs: [
          'Health checks confirm configuration exists. A real client request confirms the resolver chooses the rule you intended. Test at least one staging and production identity after changing priorities or compatibility ranges.'
        ]
      }
    ]
  },
  {
    id: 'domains',
    category: 'Release configuration',
    title: 'Configure domains, CORS and deep links',
    status: 'available',
    minutes: 7,
    summary:
      'Prove domain ownership and bind web origins to the correct native identity.',
    tags: ['domain', 'cors', 'deep link', 'universal links', 'android'],
    related: ['runtime-rules'],
    sections: [
      {
        id: 'ownership',
        title: 'Claim and verify every public origin',
        paragraphs: [
          'Verification prevents one tenant from registering another tenant’s domain. Configure the required DNS or well-known proof, then verify before relying on the domain for runtime access.'
        ]
      },
      {
        id: 'native',
        title: 'Native deep-link identity',
        table: {
          headers: ['Platform', 'Required identity'],
          rows: [
            ['iOS', 'Bundle ID and Apple Team ID'],
            ['Android', 'Package name and signing certificate fingerprints'],
            ['Web', 'Verified hostname and allowed origin']
          ]
        }
      },
      {
        id: 'safety',
        title: 'Operational checks',
        bullets: [
          'Use HTTPS origins only outside local development.',
          'Test Universal Links and Android App Links on physical devices.',
          'Keep staging and production hostnames distinct.',
          'Rotate signing identities deliberately and preserve old fingerprints during rollout when needed.'
        ]
      }
    ]
  },
  {
    id: 'clickup',
    category: 'Integrations',
    title: 'Understand the ClickUp integration',
    status: 'available',
    minutes: 7,
    summary:
      'Decide scope and ownership, then connect task data to delivery journeys safely.',
    tags: ['clickup', 'tasks', 'setup', 'requirements', 'issue tracker'],
    related: ['clickup-workspace', 'clickup-connect', 'clickup-correlation'],
    sections: [
      {
        id: 'outcome',
        title: 'What the integration is for',
        paragraphs: [
          'ClickUp gives Cindro the intent that exists before code: what the team is trying to deliver, who owns it, and whether it is planned, active, blocked or believed complete. Cindro then joins that intent to branches, pull requests, builds, deployments, OTA releases and store evidence.',
          'The task is one source of evidence—not the delivery record itself. One developer may own several tasks, one task may create changes in several repositories, and several tasks may converge into one release journey.'
        ],
        note: {
          tone: 'success',
          title: 'Lifecycle sync available',
          body: 'OAuth, Space selection, signed webhooks, automatic List discovery and per-List lifecycle mapping are live. Write-back is opt-in and advances tasks only from observed delivery evidence.'
        }
      },
      {
        id: 'decisions',
        title: 'Decisions to make before setup',
        table: {
          headers: ['Decision', 'Recommended starting point', 'Why'],
          rows: [
            [
              'Connected scope',
              'One or more engineering Spaces',
              'Cindro discovers every active List in that scope'
            ],
            [
              'Task granularity',
              'Independently deliverable outcomes',
              'Avoids one task becoming a permanent container for unrelated releases'
            ],
            [
              'Lifecycle authority',
              'ClickUp owns intent; provider evidence owns delivery truth',
              'A task status must not manufacture a deployment'
            ],
            [
              'Correlation handle',
              'Stable ClickUp task ID such as CU-1234',
              'Survives renames and remains human-readable'
            ],
            [
              'Write-back',
              'Enable after reviewing recommendations',
              'Cindro adapts to each existing List instead of requiring identical status names'
            ]
          ]
        }
      },
      {
        id: 'data-boundary',
        title: 'Data the connector records today',
        bullets: [
          'Authorized Workspace ID and the selected Space IDs used to enforce scope.',
          'Task ID, custom task ID when available, name and ClickUp URL.',
          'Current status, first assignee, creation time and closure time.',
          'Space and List identifiers needed to place the task in its configured scope.',
          'Task descriptions, comments, attachments and arbitrary custom-field values are not ingested.'
        ],
        note: {
          tone: 'info',
          title: 'Deliberately narrow data boundary',
          body: 'Additional task relationships or metadata should only be added when a concrete delivery-correlation use case requires them.'
        }
      },
      {
        id: 'setup-path',
        title: 'Current setup and maturity path',
        steps: [
          {
            title: 'Connect and choose scope',
            detail:
              'Authorize the intended Workspace, select Spaces and let Cindro register the signed webhook.'
          },
          {
            title: 'Accept recommended lifecycle mappings',
            detail:
              'Cindro discovers Lists and maps familiar status names automatically; only ambiguous statuses need a choice.'
          },
          {
            title: 'Enable lifecycle sync',
            detail:
              'Cindro uses each List’s native statuses and advances tasks to Active, Validation and Delivered when evidence warrants it.'
          },
          {
            title: 'Verify live evidence',
            detail:
              'Change a representative task and confirm ClickUp and Work Radar remain aligned.'
          },
          {
            title: 'Repair missed evidence',
            detail:
              'Use Repair gaps in Work Radar to scan one bounded page per tracked List from the last 90 days. Webhooks remain the normal fast path.'
          }
        ]
      }
    ]
  },
  {
    id: 'clickup-workspace',
    category: 'Integrations',
    title: 'Prepare your ClickUp workspace',
    status: 'blueprint',
    minutes: 9,
    summary:
      'Choose the hierarchy, statuses and task conventions Cindro will observe.',
    tags: [
      'clickup',
      'space',
      'folder',
      'list',
      'statuses',
      'board',
      'subtasks'
    ],
    related: ['clickup', 'clickup-correlation', 'repository-layout'],
    sections: [
      {
        id: 'hierarchy',
        title: 'Choose an explicit connector scope',
        paragraphs: [
          'ClickUp organizes work as Workspace → Space → Folder (optional) → List → task. Cindro will connect at Workspace level for authorization, then include only configured Spaces or Lists in delivery tracking.'
        ],
        table: {
          headers: ['Level', 'Cindro recommendation'],
          rows: [
            [
              'Workspace',
              'Authorize the Workspace containing engineering delivery work'
            ],
            [
              'Space',
              'Use an Engineering or Product Delivery Space when teams share lifecycle rules'
            ],
            [
              'Folder',
              'Use for product or programme grouping; do not depend on it for lifecycle'
            ],
            ['List', 'Use for a stable work stream, team or product scope'],
            [
              'Task',
              'One independently understandable outcome; it may touch several repositories'
            ]
          ]
        }
      },
      {
        id: 'statuses',
        title: 'Map every status to a lifecycle state',
        table: {
          headers: ['Cindro state', 'Common ClickUp statuses', 'Meaning'],
          rows: [
            [
              'Planned',
              'Backlog, To Do',
              'Accepted intent with no active engineering work'
            ],
            [
              'Ready',
              'Ready, Selected, Queued',
              'Ready for a developer to start'
            ],
            [
              'Active',
              'In Progress, Development',
              'A person is actively responsible'
            ],
            ['Blocked', 'Blocked, On Hold', 'Intent is explicitly blocked'],
            [
              'Validation',
              'In Review, QA, Staging',
              'Code may be merged but delivery is not confirmed'
            ],
            [
              'Delivered',
              'Released, Done, Closed',
              'Every required delivery path is complete'
            ],
            [
              'Cancelled',
              'Cancelled, Won’t Do',
              'No active journey should remain'
            ]
          ]
        },
        note: {
          tone: 'info',
          title: 'Existing Lists do not need to match',
          body: 'Cindro recommends mappings from names and ClickUp status types, then asks only about ambiguous statuses. Bug and Sprint Lists can keep different wording.'
        }
      },
      {
        id: 'tasks',
        title: 'Use tasks, subtasks and relationships deliberately',
        bullets: [
          'Use one task when several repositories collectively deliver one outcome.',
          'Use subtasks when parts are independently owned or independently shippable.',
          'Use task relationships when distinct tasks are expected to converge into one release.',
          'Keep the task open through review and staging; a merged pull request is not proof of delivery.',
          'Do not create repository-specific duplicate tasks merely to make linking easier.'
        ]
      },
      {
        id: 'ownership',
        title: 'Required operating conventions',
        bullets: [
          'Assign at least one owner before a task enters Active.',
          'Use a stable task ID in the branch name or pull-request title/body.',
          'Give blocked tasks a visible Blocked status rather than relying only on a comment.',
          'Preserve completed and cancelled tasks long enough for audit and reconciliation.',
          'Keep participating Lists on compatible lifecycle mappings even when their display names differ.'
        ],
        code: {
          label: 'Recommended branch and pull request convention',
          value:
            'feat/CU-1234-coach-availability\n\nPR title: CU-1234 Add coach availability controls'
        }
      },
      {
        id: 'inventory',
        title: 'Information to collect before connecting',
        table: {
          headers: ['Item', 'Example'],
          rows: [
            ['Workspace name and ID', 'Nerds Cooking · 123456'],
            ['Included Spaces/Lists', 'Engineering / Skillora Delivery'],
            ['Excluded work', 'HR, Sales, personal Lists'],
            ['Status mapping owner', 'Engineering Operations'],
            ['Task ID convention', 'CU-1234 in branch or PR'],
            ['Initial backfill window', 'Open tasks plus last 90 days']
          ]
        }
      }
    ]
  },
  {
    id: 'clickup-connect',
    category: 'Integrations',
    title: 'Connect ClickUp securely',
    status: 'available',
    minutes: 10,
    summary:
      'Set up the shared OAuth application once, then let each workspace connect itself safely.',
    tags: ['clickup', 'oauth', 'token', 'webhook', 'signature', 'rate limit'],
    related: ['clickup-workspace', 'clickup-rollout', 'operational-readiness'],
    sections: [
      {
        id: 'platform-bootstrap',
        title: 'One-time setup for the Cindro platform operator',
        paragraphs: [
          'This step is performed once for a Cindro deployment. It is not repeated by every customer. In ClickUp, open the Workspace settings, choose ClickUp API in the Integrations & ClickApps section, open the ClickUp API Settings tab, then select + Create an App. The App Center installs existing integrations and cannot create this OAuth application.',
          'Name the app Cindro and register the exact callback URL for the deployment. For production, use https://cindro.nerds.cooking/api/pipeline/clickup/oauth/callback. Copy the generated Client ID and Client Secret into the protected cindro-prod deployment secrets; never place them in source control or customer-visible settings.'
        ],
        steps: [
          {
            title: 'Open Workspace settings',
            detail: 'The ClickUp user must be a Workspace owner or admin.'
          },
          {
            title: 'Open ClickUp API',
            detail:
              'Use Integrations & ClickApps → ClickUp API → ClickUp API Settings, not App Center.'
          },
          {
            title: 'Create the OAuth app',
            detail:
              'Select + Create an App, name it Cindro and enter the exact deployment callback URL.'
          },
          {
            title: 'Configure the deployment',
            detail:
              'Store the Client ID and Client Secret as protected platform deployment secrets, then redeploy Cindro.'
          }
        ],
        code: {
          label: 'Production OAuth callback URL',
          value:
            'https://cindro.nerds.cooking/api/pipeline/clickup/oauth/callback'
        },
        note: {
          tone: 'info',
          title: 'Do not create a webhook in ClickUp',
          body: 'Cindro creates the correctly scoped webhook after a tenant administrator completes authorization and chooses its Spaces.'
        }
      },
      {
        id: 'authentication',
        title: 'Self-service setup for each Cindro workspace',
        paragraphs: [
          'After the shared OAuth application is configured, customers do not create ClickUp apps, configure callback URLs, supply environment variables or send secrets to the Cindro team.',
          'A ClickUp Workspace owner or admin opens Cindro Delivery model, selects Connect ClickUp, approves the intended Workspace in ClickUp, returns to Cindro, chooses that Workspace and selects one or more Spaces. Saving the connection creates the webhook and starts read-only task observation.'
        ],
        table: {
          headers: ['Part', 'Who manages it'],
          rows: [
            [
              'Cindro OAuth application',
              'Cindro platform operator; configured once per deployment'
            ],
            [
              'Workspace authorization',
              'Customer admin; completed through Connect ClickUp'
            ],
            ['Observed scope', 'Customer admin; one or more ClickUp Spaces'],
            [
              'Workspace access token',
              'Cindro; encrypted per tenant and never exposed after authorization'
            ],
            [
              'Webhook and signing secret',
              'Cindro; created and stored automatically'
            ],
            [
              'Disconnect or reauthorize',
              'Customer admin; available in the ClickUp connection panel'
            ]
          ]
        },
        note: {
          tone: 'warn',
          title: 'Authorize with a durable ClickUp identity',
          body: 'ClickUp ties webhooks to the user who authorizes the connection. If that user is disabled or loses access, task events can stop. Reauthorize from Cindro using another suitable workspace administrator.'
        }
      },
      {
        id: 'webhook',
        title: 'What Cindro configures today',
        paragraphs: [
          'Cindro registers a Space-scoped webhook after OAuth succeeds. ClickUp returns a unique secret and signs webhook request bodies with HMAC-SHA256 in the X-Signature header; Cindro verifies that signature before accepting an event.'
        ],
        bullets: [
          'Selected Spaces define the observed scope and Cindro discovers their active Lists.',
          'Task identity, title, URL, status, assignees and hierarchy identifiers become delivery evidence.',
          'Per-List status mappings preserve different Bug and Sprint workflows behind one neutral lifecycle.',
          'Optional write-back advances existing tasks to the mapped Active, Validation or Delivered status; it never moves a task between Lists.',
          'Disconnecting removes the active webhook while preserving evidence already recorded in Cindro.'
        ]
      },
      {
        id: 'recovery',
        title: 'Repair missed task evidence',
        paragraphs: [
          'Work Radar exposes the last successful ClickUp repair and a Repair gaps action. A repair reads at most one recent page—up to 100 tasks—from each tracked List, restricted to tasks updated in the last 90 days. Lists marked Ignored are never scanned.',
          'The repair is deliberately operator-triggered. Cindro does not continuously poll the Workspace or start overlapping background scans, so a missed webhook can be recovered without turning ClickUp into the primary read path.'
        ],
        bullets: [
          'Webhook events remain the normal low-latency path.',
          'Reconciliation upserts provider-neutral task evidence idempotently.',
          'A successful run records its completion time and scanned/synchronized counts.',
          'Authorization and provider failures are retained on connector health.',
          'Automatic scheduling, rate-limit header telemetry and webhook-suspension diagnostics remain future safeguards.'
        ],
        note: {
          tone: 'success',
          title: 'Safe bounded recovery is live',
          body: 'Use Repair gaps after an outage, authorization change or suspected missed event. It will not alter ClickUp unless lifecycle write-back was separately enabled.'
        }
      },
      {
        id: 'limits',
        title: 'Respect plan-dependent API limits',
        paragraphs: [
          'ClickUp rate limits requests per token. Cindro keeps reconciliation sequential, bounded to one page per tracked List and restricted to a 90-day update window; it does not continuously poll the Workspace.',
          'A failed provider request stops the repair and remains visible as connector health. Future automation should also expose remaining-limit headers and explicit backoff state before scheduled reconciliation is enabled.'
        ],
        note: {
          tone: 'info',
          title: 'Webhooks are the fast path',
          body: 'Normal task changes arrive by webhook. Reconciliation exists only to backfill and repair evidence.'
        }
      },
      {
        id: 'configuration',
        title: 'The self-service setup available today',
        table: {
          headers: ['Step', 'Customer choice'],
          rows: [
            [
              'Connect',
              'Approve Cindro in ClickUp; no credentials are copied or pasted'
            ],
            ['Scope', 'Choose a Workspace and one or more Spaces'],
            [
              'Lists',
              'Track delivery Lists and ignore planning or operating-cadence Lists'
            ],
            [
              'Map',
              'Accept automatic List mappings and resolve only ambiguous statuses'
            ],
            [
              'Sync',
              'Save the mapping only or enable conservative task updates'
            ],
            [
              'Verify',
              'Change a representative task and confirm Last event updates'
            ]
          ]
        },
        note: {
          tone: 'success',
          title: 'Customer setup stays inside Cindro',
          body: 'Cindro adapts to existing Lists and status names. Customers do not need to recreate their ClickUp workflow.'
        }
      },
      {
        id: 'sources',
        title: 'Official ClickUp references',
        links: [
          {
            label: 'Create an OAuth app',
            href: 'https://help.clickup.com/hc/en-us/articles/6303422883095-Create-your-own-app-with-the-ClickUp-API',
            detail:
              'Current ClickUp settings navigation, permissions and app creation.'
          },
          {
            label: 'Authentication',
            href: 'https://developer.clickup.com/docs/authentication',
            detail: 'OAuth application and authorization-code flow.'
          },
          {
            label: 'Webhooks',
            href: 'https://developer.clickup.com/docs/webhooks',
            detail: 'Scope, event types, idempotency and delivery behavior.'
          },
          {
            label: 'Webhook signatures',
            href: 'https://developer.clickup.com/docs/webhooksignature',
            detail: 'HMAC-SHA256 verification using the raw request body.'
          },
          {
            label: 'Webhook health',
            href: 'https://developer.clickup.com/docs/webhookhealth',
            detail: 'Retries, suspension and response-time expectations.'
          },
          {
            label: 'Rate limits',
            href: 'https://developer.clickup.com/docs/rate-limits',
            detail: 'Per-token limits and reset headers.'
          }
        ]
      }
    ]
  },
  {
    id: 'plane-connect',
    category: 'Integrations',
    title: 'Connect Plane with signed updates',
    status: 'available',
    minutes: 6,
    summary:
      'Send scoped Plane work-item events into Cindro without sharing a Plane API key.',
    tags: [
      'plane',
      'self-hosted',
      'webhook',
      'work items',
      'tasks',
      'project scope',
      'HMAC'
    ],
    related: ['clickup-connect', 'work-radar', 'delivery-tracks'],
    sections: [
      {
        id: 'connection',
        title: 'Create a Plane webhook',
        steps: [
          {
            title: 'Set the project scope in Cindro',
            detail:
              'Open Delivery setup → Work trackers → Plane. Add each project as KEY=UUID, for example SKL=<project UUID>. Use a short key with letters and numbers. Cindro uses it to recognize task references and the UUID to reject events from other projects.'
          },
          {
            title: 'Create a v2 workspace webhook in Plane',
            detail:
              'Open Workspace settings → Webhooks. Create a new v2 webhook for work-item created, updated and deleted events, then paste the Cindro webhook address shown in the setup panel.'
          },
          {
            title: 'Save the signing secret',
            detail:
              'Copy the signing secret Plane shows when you create the webhook, then paste it into Cindro. If you lose it, open the webhook in Plane to reveal or regenerate the secret, then update Cindro.'
          },
          {
            title: 'Check the first event',
            detail:
              'Change a task in one selected project. Cindro should show the time of the latest accepted Plane webhook on the connection. If it does not arrive, open the webhook delivery log in Plane.'
          }
        ]
      },
      {
        id: 'delivery',
        title: 'Check delivery when updates stop',
        paragraphs: [
          'Plane accepts a delivery when Cindro returns 2xx. Plane does not retry 4xx responses, including a bad signature. Fix the secret in Cindro, then send a new test event.',
          'Plane retries server errors with backoff. After five failed attempts, it disables the webhook. Fix the cause, re-enable the webhook in Plane, and check its delivery log before testing again.'
        ],
        note: {
          tone: 'info',
          title: 'A saved connection is not proof of delivery',
          body: 'Cindro shows "Configured · waiting for first event" until a signed webhook reaches it.'
        }
      },
      {
        id: 'scope',
        title: 'What Cindro records',
        bullets: [
          'Work-item ID, project key and UUID, title, task reference, timestamps, and the native Plane link.',
          'Assignee IDs are preserved as Plane identities. Cindro does not guess a person’s name when Plane does not include it.',
          'State is mapped only when Plane supplies a recognized state group. Otherwise the Cindro status remains Unknown.'
        ],
        note: {
          tone: 'info',
          title: 'Project scope is enforced twice',
          body: 'Cindro accepts only the project UUIDs you listed. You can also filter the workspace webhook in Plane to reduce events sent to Cindro.'
        }
      },
      {
        id: 'limits',
        title: 'What this first connection does not do',
        bullets: [
          'It starts with new events; it does not import old work items or reconcile missed webhook deliveries.',
          'Cindro does not call the Plane API or change Plane tasks. No Plane API key is requested.',
          'You can link a Plane execution task to a planning task in an expanded Work Radar journey. The link appears on both tasks; it does not combine their status, owner or delivery journey.',
          'To stop delivery, disconnect Plane in Cindro and remove the webhook in Plane. Existing Cindro history is kept.'
        ],
        note: {
          tone: 'warn',
          title: 'Keep the secret private',
          body: 'The webhook signing secret is encrypted in Cindro and is never displayed again. If it is lost, rotate it in Plane and update the value in Cindro.'
        }
      },
      {
        id: 'references',
        title: 'Plane setup and delivery docs',
        links: [
          {
            label: 'Create a webhook',
            href: 'https://developers.plane.so/api-reference/v2/webhooks/create-webhook',
            detail: 'Webhook setup and signing-secret behavior.'
          },
          {
            label: 'Webhook delivery and signatures',
            href: 'https://developers.plane.so/dev-tools/intro-webhooks',
            detail:
              'Payload signatures, response codes, retries and delivery logs.'
          }
        ]
      }
    ]
  },
  {
    id: 'clickup-correlation',
    category: 'Integrations',
    title: 'Map ClickUp work to delivery journeys',
    status: 'blueprint',
    minutes: 9,
    summary:
      'Define deterministic correlation, lifecycle truth and useful divergence findings.',
    tags: [
      'clickup',
      'correlation',
      'journey',
      'branch',
      'pull request',
      'status mapping'
    ],
    related: ['clickup-workspace', 'work-radar', 'delivery-tracks'],
    sections: [
      {
        id: 'identity',
        title: 'Correlation starts with a stable task identity',
        paragraphs: [
          'Cindro normalizes provider tasks as distinct work items keyed by their provider IDs. Names, statuses and assignees can change without creating a new work item.'
        ],
        table: {
          headers: ['Evidence', 'Strength', 'Behavior'],
          rows: [
            [
              'Exact task ID in branch or PR',
              'Deterministic',
              'Use as a correlation key; do not merge unrelated task records'
            ],
            [
              'Explicit task relationship configured in Cindro',
              'Human-confirmed',
              'Show the link on both tasks; keep journeys, owners and statuses separate'
            ],
            [
              'Provider task URL in PR body',
              'Deterministic',
              'Resolve URL to the provider task when supported'
            ],
            [
              'Similar title or same assignee',
              'Suggestive only',
              'Offer as a possible match; never attach silently'
            ],
            ['Shared timestamp', 'Insufficient', 'Do not correlate']
          ]
        }
      },
      {
        id: 'convergence',
        title: 'Represent splits and convergence without losing work',
        bullets: [
          'One task can link to several pull requests across application, deployment and infrastructure repositories.',
          'Several tasks can feed one journey when they are intentionally shipped together.',
          'A subtask may inherit context from its parent, but completion remains independent.',
          'A downstream release can contain several journeys; it does not erase their original task ownership.',
          'Merging journeys requires explicit evidence or a human decision, never title similarity alone.'
        ]
      },
      {
        id: 'truth',
        title: 'Separate reported status from observed delivery',
        table: {
          headers: ['Situation', 'Cindro interpretation'],
          rows: [
            [
              'ClickUp Done, no production evidence',
              'Task says done; delivery unverified'
            ],
            [
              'Production evidence, ClickUp still Active',
              'Delivered; task lifecycle stale'
            ],
            [
              'PR merged, task In Review',
              'Reasonable while awaiting deployment'
            ],
            [
              'Task Cancelled, active PR remains',
              'Contradiction requiring owner attention'
            ],
            [
              'Task Blocked, automation continues',
              'Show both facts; do not infer resolution'
            ]
          ]
        }
      },
      {
        id: 'findings',
        title: 'Findings Work Radar should create',
        table: {
          headers: ['Finding', 'Default owner', 'Suggested threshold'],
          rows: [
            [
              'Active task has no source change',
              'Task assignee',
              '2 working days'
            ],
            ['Open PR has no task reference', 'PR author', 'Immediately'],
            [
              'Merged work has no deployment evidence',
              'Automation owner',
              'Track-specific SLA'
            ],
            [
              'Production evidence exists but task is not Done',
              'Task assignee',
              '1 working day'
            ],
            [
              'Task is Done without production evidence',
              'Task assignee',
              'Immediately'
            ],
            [
              'Cancelled task still has active delivery work',
              'Task assignee and PR author',
              'Immediately'
            ]
          ]
        },
        note: {
          tone: 'info',
          title: 'Thresholds are policy, not universal truth',
          body: 'Cindro will ship sensible defaults but allow each delivery track to tune time-based findings for its actual cadence.'
        }
      },
      {
        id: 'writeback',
        title: 'Conservative write-back is opt-in',
        paragraphs: [
          'After reviewing the recommended mappings, a workspace admin can enable lifecycle sync. Cindro writes the destination status native to each task’s own List, so Bug and Sprint workflows do not need identical names.'
        ],
        bullets: [
          'Never mark Delivered from merge evidence alone.',
          'Require every applicable production path in the active delivery track to complete.',
          'Advance lifecycle only; never move a task backwards or between Lists.',
          'Treat Cindro-originated webhook echoes idempotently.',
          'Keep failure and missing-evidence states in Cindro unless Blocked write-back is explicitly enabled.'
        ]
      }
    ]
  },
  {
    id: 'clickup-rollout',
    category: 'Integrations',
    title: 'Verify and roll out the ClickUp connector',
    status: 'blueprint',
    minutes: 8,
    summary:
      'Prove scope, correlation and webhook health before findings affect the team.',
    tags: ['clickup', 'rollout', 'testing', 'backfill', 'acceptance criteria'],
    related: [
      'clickup-connect',
      'clickup-correlation',
      'operational-readiness'
    ],
    sections: [
      {
        id: 'before',
        title: 'Pre-connection checklist',
        bullets: [
          'A ClickUp Workspace owner or admin can authorize the existing Cindro integration.',
          'Included Spaces or Lists and excluded work are written down.',
          'Every reachable status has a lifecycle mapping.',
          'The team has agreed where task IDs appear in branches and pull requests.',
          'A bounded initial backfill window has been chosen.',
          'No environment variables, callback URLs or credentials need to be supplied by the customer.'
        ]
      },
      {
        id: 'proof',
        title: 'Run a representative proof journey',
        steps: [
          {
            title: 'Create or select a real task',
            detail:
              'Use an in-scope List, assign an owner and move it into an Active-mapped status.'
          },
          {
            title: 'Open changes in two repositories',
            detail:
              'Carry the same task ID into application and deployment repository branches or pull requests.'
          },
          {
            title: 'Change ownership and status',
            detail:
              'Confirm signed webhook events update the normalized work item once.'
          },
          {
            title: 'Ship through staging and production',
            detail:
              'Confirm ClickUp status and provider evidence remain distinct but visible in one journey.'
          },
          {
            title: 'Exercise a contradiction',
            detail:
              'Mark the task Done without production evidence and verify Work Radar creates the expected finding.'
          }
        ]
      },
      {
        id: 'acceptance',
        title: 'Connector acceptance criteria',
        bullets: [
          'Only configured hierarchy is imported.',
          'A task update becomes visible within the agreed webhook latency.',
          'Duplicate webhook delivery does not duplicate work items or findings.',
          'A missed webhook is repaired by reconciliation.',
          'Disabling the authorizing ClickUp user produces a visible connection-health failure.',
          'Multi-repository changes attach to one task journey.',
          'Several tasks can converge without losing individual ownership.',
          'No ClickUp data is changed during read-only rollout.'
        ]
      },
      {
        id: 'operations',
        title: 'Operational signals Cindro must expose',
        table: {
          headers: ['Signal', 'Healthy state'],
          rows: [
            ['OAuth connection', 'Authorized for the configured Workspace'],
            ['Webhook', 'Active; last receipt visible'],
            ['Signature verification', 'No unexplained failures'],
            ['Reconciliation', 'Last run and next run visible'],
            ['Rate limit', 'Remaining capacity and backoff state visible'],
            ['Mapping coverage', 'No in-scope unmapped statuses'],
            ['Import scope', 'Counts by configured Space/List']
          ]
        }
      },
      {
        id: 'enable',
        title: 'Enable findings gradually',
        steps: [
          {
            title: 'Shadow mode',
            detail:
              'Build journeys and diagnostics without showing team findings.'
          },
          {
            title: 'Advisory mode',
            detail:
              'Show ClickUp divergence in Work Radar but send no notifications.'
          },
          {
            title: 'Operational mode',
            detail:
              'Enable selected notifications once false positives are reviewed.'
          },
          {
            title: 'Future write-back',
            detail:
              'Consider task updates only as a separate, explicitly approved capability.'
          }
        ],
        note: {
          tone: 'success',
          title: 'Ready for implementation',
          body: 'Once this checklist reflects your actual ClickUp hierarchy and status names, we can implement the connector without making product decisions in the middle of authentication and webhook work.'
        }
      }
    ]
  },
  {
    id: 'app-stores',
    category: 'Integrations',
    title: 'Prepare App Store Connect and Google Play',
    status: 'blueprint',
    minutes: 8,
    summary:
      'Credentials, tracks and review states required for native delivery visibility.',
    tags: ['apple', 'app store', 'google play', 'fastlane', 'review'],
    related: ['app-versions', 'work-radar'],
    sections: [
      {
        id: 'boundary',
        title: 'Use store stages only for native changes',
        paragraphs: [
          'Most web or hybrid changes should remain on the OTA track. Apply native build and store-review stages only when iOS, Android, Capacitor configuration or native plugins change.'
        ]
      },
      {
        id: 'apple',
        title: 'App Store Connect',
        bullets: [
          'Create a least-privilege API key for the application.',
          'Record issuer ID, key ID and private key securely.',
          'Map the bundle ID to the Cindro app.',
          'Choose TestFlight groups and production release policy explicitly.'
        ]
      },
      {
        id: 'google',
        title: 'Google Play',
        bullets: [
          'Create a dedicated service account with application-scoped release access.',
          'Grant access to the intended package only.',
          'Map internal, closed, open and production tracks.',
          'Decide whether production rollout is immediate or percentage-based.'
        ]
      },
      {
        id: 'polling',
        title: 'Review status must be polled',
        paragraphs: [
          'Neither store provides a complete real-time webhook for review outcomes. Cindro will use scheduled polling and represent the stage as waiting on an external provider—not as missing or failed evidence.'
        ],
        note: {
          tone: 'warn',
          title: 'Never store credentials in a pipeline definition',
          body: 'Definitions reference connector resources. Secrets belong in encrypted connector configuration and must be redacted from logs and audit payloads.'
        }
      }
    ]
  },
  {
    id: 'kubernetes-runtime',
    category: 'Operations',
    title: 'Connect Kubernetes runtime evidence securely',
    status: 'available',
    minutes: 8,
    summary:
      'Install the outbound-only Prometheus observer and understand exactly what it can—and cannot—access.',
    tags: [
      'kubernetes',
      'prometheus',
      'kube-state-metrics',
      'runtime',
      'security',
      'pods',
      'rollout'
    ],
    related: ['operational-readiness', 'repository-layout', 'environments'],
    sections: [
      {
        id: 'boundary',
        title: 'The security boundary',
        paragraphs: [
          'Cindro never connects inbound to your cluster or Prometheus. A small pod queries Prometheus inside your network and sends a bounded normalized snapshot to Cindro over HTTPS.',
          'One credential is bound server-side to one Cindro project, environment and Kubernetes namespace. It is write-only: it cannot read Cindro data, trigger a workflow, change a release or select a different tenant scope.'
        ],
        bullets: [
          'No Service or inbound listener.',
          'No Kubernetes API token is mounted.',
          'No custom PromQL, remote command or dynamic query input.',
          'Non-root process, read-only root filesystem, dropped Linux capabilities and RuntimeDefault seccomp.',
          'Credential expires after 180 days and can be rotated or revoked immediately.'
        ]
      },
      {
        id: 'prerequisites',
        title: 'Cluster prerequisites',
        bullets: [
          'Prometheus is reachable from the observer namespace.',
          'Use stable Kubernetes Service DNS for in-cluster Prometheus—not a reallocatable ClusterIP.',
          'Use the HTTPS FQDN for Cindro; never send the observer credential to a plaintext internal service.',
          'kube-state-metrics exposes Deployment, StatefulSet, DaemonSet, pod-owner, container-image and restart metrics.',
          'The workload names declared in release-candidate manifests match the Kubernetes controller names.'
        ],
        note: {
          tone: 'info',
          title: 'GlitchTip and OpenTelemetry stay separate',
          body: 'Prometheus proves Kubernetes rollout and replica state. GlitchTip, OpenTelemetry and synthetic checks describe application behaviour; Cindro does not blend them into an opaque health score.'
        }
      },
      {
        id: 'install',
        title: 'Install from Release Control',
        steps: [
          {
            title: 'Open Release Control → Release state',
            detail:
              'Choose Connect Prometheus. Select the exact Cindro project, environment and Kubernetes namespace.'
          },
          {
            title: 'Issue the one-time credential',
            detail:
              'Copy it once. Cindro stores only a slow password hash and the visible non-secret prefix.'
          },
          {
            title: 'Use the generated silent-prompt command',
            detail:
              'The command creates a Kubernetes Secret without putting the token in shell history, then installs the hardened Helm chart.'
          },
          {
            title: 'Confirm the first snapshot',
            detail:
              'Release Control changes from Awaiting data to Connected and reports each declared workload as ready, rolling, degraded, stale or unseen.'
          }
        ],
        note: {
          tone: 'warn',
          title: 'Never store the token in Git or Helm values',
          body: 'Do not paste it into a manifest, command argument, screenshot or log. If it is exposed, rotate it in Release Control and update the Kubernetes Secret immediately.'
        }
      },
      {
        id: 'evidence',
        title: 'What Cindro records',
        table: {
          headers: ['Evidence', 'Use'],
          rows: [
            [
              'Controller replicas',
              'Desired, ready, available and updated rollout state'
            ],
            [
              'Workload identity',
              'Distinguishes services even when several controllers run the same image'
            ],
            [
              'Containers',
              'Image reference, immutable image ID and aggregate restart count'
            ],
            [
              'Freshness',
              'Snapshots older than five minutes become Unknown, never Healthy'
            ],
            [
              'Delivery identity',
              'Kept separate from runtime evidence so a merged GitOps PR cannot imply healthy pods'
            ]
          ]
        }
      },
      {
        id: 'operations',
        title: 'Operate and recover safely',
        bullets: [
          'Rotate a credential on a regular schedule or immediately after suspected exposure.',
          'Revoke observers that no longer own a namespace.',
          'Investigate Awaiting data from pod logs and Prometheus reachability; Cindro never falls back to guessing health.',
          'For dynamic or proxied public endpoints, enforce egress with a CNI that supports FQDN policies instead of copying transient DNS answers into CIDR rules.',
          'Keep provider-native kubectl, GitOps and rollback procedures available as the break-glass path.'
        ]
      },
      {
        id: 'sources',
        title: 'Official references',
        links: [
          {
            label: 'Prometheus HTTP API',
            href: 'https://prometheus.io/docs/prometheus/latest/querying/api/',
            detail: 'The fixed instant-query endpoint used by the observer.'
          },
          {
            label: 'Prometheus security model',
            href: 'https://prometheus.io/docs/operating/security/',
            detail:
              'Why Prometheus should remain private inside the cluster network.'
          },
          {
            label: 'kube-state-metrics metrics',
            href: 'https://github.com/kubernetes/kube-state-metrics/tree/main/docs',
            detail:
              'Controller, pod-owner, container-image and restart metric contracts.'
          }
        ]
      }
    ]
  },
  {
    id: 'work-radar',
    category: 'Operations',
    title: 'Use Work Radar as the daily operational inbox',
    status: 'available',
    minutes: 5,
    summary:
      'Triage what needs a person, a system or an external provider without losing work.',
    tags: ['radar', 'triage', 'blocked', 'stale', 'attention'],
    related: ['delivery-tracks', 'clickup', 'operational-readiness'],
    sections: [
      {
        id: 'routine',
        title: 'Recommended daily routine',
        steps: [
          {
            title: 'Start with My work',
            detail:
              'This view contains only direct Cindro assignments or provider identities verified to your email.'
          },
          {
            title: 'Triage Team queue and Unowned',
            detail:
              'Claim or route recovery work that cannot yet be attributed to one person.'
          },
          {
            title: 'Use Approvals for human gates',
            detail:
              'Requested reviewers, requested teams and manually gated stages remain distinct from automation failures.'
          },
          {
            title: 'Use Radar for flow',
            detail:
              'Select a delivery stage to inspect where work is accumulating; use technical history only for forensic work.'
          }
        ]
      },
      {
        id: 'ownership',
        title: 'Read ownership literally',
        table: {
          headers: ['Label', 'Interpretation'],
          rows: [
            [
              'My work',
              'Directly assigned in Cindro or matched by an exact verified provider email'
            ],
            [
              'Suggested owner',
              'A task assignee, pull-request author or reviewer can be identified, but the finding is a system recovery'
            ],
            [
              'Unmapped person',
              'The provider supplied an identity that has not been linked to a Cindro member'
            ],
            [
              'Unowned',
              'No task assignment, source author, review request or direct Cindro claim is available'
            ],
            [
              'External provider',
              'A provider such as an app store currently owns the wait'
            ]
          ]
        }
      },
      {
        id: 'actions',
        title: 'Safe action rules',
        bullets: [
          'Run step is available only for a definition-bound manual connector action.',
          'Record done creates auditable human evidence but does not change an external system.',
          'Snooze hides a known finding temporarily; it does not mark the stage complete.',
          'Resolve suppresses the current condition until it clears and later recurs.'
        ]
      }
    ]
  },
  {
    id: 'operational-readiness',
    category: 'Operations',
    title: 'Prove a delivery model before automation',
    status: 'available',
    minutes: 6,
    summary:
      'A release-readiness checklist for new projects and connector changes.',
    tags: ['readiness', 'test', 'automation', 'safety'],
    related: ['start-here', 'work-radar', 'github-connection'],
    sections: [
      {
        id: 'proof',
        title: 'Run one real change end to end',
        bullets: [
          'Cindro correlates the intended task and source changes.',
          'CI evidence attaches to the correct journey.',
          'The deployment repository change is observed.',
          'Staging evidence names the exact environment.',
          'The production gate is human-owned and actionable.',
          'The final production artifact or OTA bundle is exact.'
        ]
      },
      {
        id: 'failure',
        title: 'Exercise failure states',
        bullets: [
          'Fail a check and confirm it outranks downstream activity.',
          'Leave expected evidence absent long enough to create an overdue finding.',
          'Close an unmerged PR and confirm attention resolves.',
          'Snooze and resolve a finding, then verify its recurrence behaviour.',
          'Confirm an unauthorised user cannot dispatch a protected action.'
        ]
      },
      {
        id: 'automation',
        title: 'Enable automation last',
        paragraphs: [
          'Automatic stage-completed triggers should be introduced one non-production action at a time. Preserve provider-native approvals and never allow a client-supplied repository, workflow or pull request to become an executable target.'
        ]
      }
    ]
  }
];

export function searchGuideArticles(query: string, articles = GUIDE_ARTICLES) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return articles;
  const terms = normalized.split(/\s+/);
  return articles.filter((article) => {
    const searchableText = [
      article.title,
      article.summary,
      article.category,
      ...article.tags,
      ...article.sections.flatMap((section) => [
        section.title,
        ...(section.paragraphs || []),
        ...(section.bullets || []),
        ...(section.steps || []).flatMap((step) => [step.title, step.detail])
      ])
    ]
      .join(' ')
      .toLowerCase();
    return terms.every((term) => searchableText.includes(term));
  });
}

export function guideArticleById(id?: string) {
  return (
    GUIDE_ARTICLES.find((article) => article.id === id) || GUIDE_ARTICLES[0]
  );
}
